/*
  BRS Ring Sizer (sections/brs-ring-sizer.liquid)

  1. Card: any ID-1 card (bank, ID, gift card: 53.98 mm wide) lies on a card
     outline drawn over a green strip, and the customer resizes both to match
     it. That fixes the screen's real CSS px per mm, remembered per device.
  2. Finger: the finger rests on the strip and − / + step through US sizes; each
     size sets the strip to the width that size's knuckle looks from above. The
     size whose strip edges just touch the widest part of the finger wins.
     A ring's inside diameter equals the knuckle's caliper width
     (18.95 mm knuckle = size 9), so the strip models the knuckle directly.

  Parallax: whatever rests on the glass has its edges above the pixels, so from
  the eye it looks wider than it is: seen = width * EYE / (EYE - height).
*/
(() => {
  if (customElements.get('brs-ring-sizer')) return;

  const CARD_W = 53.98; // ISO/IEC 7810 ID-1 short side, mm
  const CARD_H = 85.6; // long side, mm (only shapes the card outline)
  const CARD_R = 3.18; // corner radius, mm (only shapes the card outline)
  const CARD_T = 0.76; // card thickness, mm
  const GLASS = 0.7; // cover glass between the pixels and what rests on it, mm
  const EYE = 350; // assumed eye height above the screen, mm
  const EDGE_K = 0.45; // a finger's widest point sits this fraction of its width above the glass
  const SIZE_MIN = 3;
  const SIZE_MAX = 16;
  const SEED_PX_PER_MM = 6; // typical phone; only seeds the card slider
  const STORAGE_KEY = 'brs-ring-sizer:v1';

  const insideDiameter = (size) => 11.6332 + 0.8128 * size; // US size to mm (0.458" + 0.032" per size)
  const seenWidth = (width, height) => (width * EYE) / (EYE - height);
  const CARD_SEEN = seenWidth(CARD_W, GLASS + CARD_T);
  const fingerSeen = (width) => seenWidth(width, GLASS + EDGE_K * width);
  const formatSize = (size) => (size % 1 ? `${Math.floor(size)}½` : `${size}`);
  const layoutWidth = () => document.documentElement.clientWidth;

  const store = {
    get() {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      } catch (error) {
        return {};
      }
    },
    set(patch) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...store.get(), ...patch }));
      } catch (error) {
        // Private browsing: the tool still works, it just won't remember the card.
      }
    },
  };

  const STEP_LABELS = {
    card: 'Card · step 1 of 2',
    measure: 'Finger · step 2 of 2',
    result: 'Your result',
  };

  const OVERLAY_HTML = `
    <div class="brs-rs-panel">
      <div class="brs-rs-panel__top">
        <p class="brs-rs-step" data-out="step"></p>
        <output class="brs-rs-size" aria-live="polite"><span>Est. US size</span><b data-out="size"></b></output>
        <button type="button" class="brs-rs-close" data-action="close" aria-label="Close ring sizer">
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
      </div>
      <div class="brs-rs-pane" data-pane="card">
        <ol class="brs-rs-steps">
          <li>Lay a standard card (bank, ID or gift card) on the outline, <strong>long side up and down</strong>.</li>
          <li>Slide until the outline matches the card edge to edge.</li>
        </ol>
        <div class="brs-rs-controls">
          <button type="button" class="brs-rs-stepper" data-stepper="card" data-dir="-1" aria-label="Narrower">&minus;</button>
          <input type="range" class="brs-rs-range" data-input="card" min="150" max="450" step="0.25" aria-label="Strip width">
          <button type="button" class="brs-rs-stepper" data-stepper="card" data-dir="1" aria-label="Wider">+</button>
        </div>
        <button type="button" class="brs-rs-primary" data-action="card-done">Card matches, next</button>
      </div>
      <div class="brs-rs-pane" data-pane="measure">
        <!-- Steppers sit at the screen edges: on short phones the fingertip reaches the panel's middle. -->
        <div class="brs-rs-controls brs-rs-controls--apart">
          <button type="button" class="brs-rs-stepper" data-stepper="size" data-dir="-1" aria-label="Smaller size">&minus;</button>
          <button type="button" class="brs-rs-stepper" data-stepper="size" data-dir="1" aria-label="Larger size">+</button>
        </div>
        <ol class="brs-rs-steps">
          <li>Rest your ring finger on the strip. <strong>Don't press.</strong></li>
          <li>Look straight down. Tap &minus; or + until the strip's edges just touch the widest part of your finger.</li>
        </ol>
        <div class="brs-rs-row">
          <button type="button" class="brs-rs-secondary" data-action="recalibrate">Redo card</button>
          <button type="button" class="brs-rs-primary" data-action="size-done">That's my size</button>
        </div>
      </div>
      <div class="brs-rs-pane" data-pane="result">
        <p class="brs-rs-result-label">Your estimated ring size</p>
        <p class="brs-rs-result"><b data-out="result"></b> <small>US</small></p>
        <p class="brs-rs-text brs-rs-text--center">Phone sizing gets you close, usually within 1 ring size. Measure twice, then confirm with a SureFit ring sizer before you order.</p>
        <a class="brs-rs-primary" data-out="sizer"></a>
        <div class="brs-rs-row brs-rs-row--center">
          <button type="button" class="brs-rs-link" data-action="measure-again">Measure again</button>
          <button type="button" class="brs-rs-link" data-action="close">Done</button>
        </div>
      </div>
    </div>
    <div class="brs-rs-field" aria-hidden="true">
      <div class="brs-rs-band"></div>
      <div class="brs-rs-card"><span>Your card here</span></div>
    </div>
    <div class="brs-rs-rotate"><p>Turn your phone upright to use the ring sizer.</p></div>
  `;

  class BrsRingSizer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.offset = parseFloat(this.dataset.offset) || 0;
      this.startButton = this.querySelector('[data-ring-sizer-start]');
      this.note = this.querySelector('[data-ring-sizer-note]');
      this.message = this.querySelector('[data-ring-sizer-message]');
      this.last = this.querySelector('[data-ring-sizer-last]');

      const usable =
        this.dataset.mobileOnly !== 'true' ||
        window.matchMedia('(pointer: coarse)').matches ||
        (window.Shopify && window.Shopify.designMode);
      this.startButton.hidden = !usable;
      if (this.note) this.note.hidden = usable;
      this.showLast(store.get().last);

      this.onResize = () => this.render();
      this.onKeydown = (event) => {
        if (event.key === 'Escape') this.close();
      };
      this.startButton.addEventListener('click', () => this.open());
    }

    disconnectedCallback() {
      if (!this.overlay) return;
      this.close();
      this.overlay.remove();
      this.overlay = null;
    }

    buildOverlay() {
      const overlay = document.createElement('div');
      overlay.className = 'brs-rs-overlay';
      overlay.hidden = true;
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', 'Ring sizer');
      overlay.innerHTML = OVERLAY_HTML;

      const sizer = overlay.querySelector('[data-out="sizer"]');
      sizer.href = this.dataset.sizerUrl || '/collections/ring-sizers';
      sizer.textContent = this.dataset.sizerLabel || 'Confirm with a SureFit ring sizer';

      overlay.addEventListener('click', (event) => {
        const target = event.target.closest('[data-action]');
        if (target) this.handle(target.dataset.action);
      });
      // iOS Safari pinch-zoom would change the screen scale mid-measurement.
      overlay.addEventListener('gesturestart', (event) => event.preventDefault());
      overlay.querySelectorAll('[data-stepper]').forEach((button) => this.bindStepper(button));

      this.cardInput = overlay.querySelector('[data-input="card"]');
      this.cardInput.addEventListener('input', () => this.setCard(parseFloat(this.cardInput.value)));
      this.field = overlay.querySelector('.brs-rs-field');
      this.band = overlay.querySelector('.brs-rs-band');
      this.cardOutline = overlay.querySelector('.brs-rs-card');

      // On <body> so no transformed ancestor can break position: fixed.
      document.body.appendChild(overlay);
      return overlay;
    }

    bindStepper(button) {
      const kind = button.dataset.stepper;
      const direction = Number(button.dataset.dir);
      let timer = null;
      let count = 0;

      const step = () => {
        count += 1;
        if (kind === 'card') this.setCard(this.cardPx + direction * (count > 8 ? 2 : 1));
        else this.setSize(this.size + direction * 0.5);
      };
      const stop = () => {
        clearTimeout(timer);
        timer = null;
      };
      const repeat = (delay) => {
        timer = setTimeout(() => {
          step();
          repeat(kind === 'card' ? 60 : 220);
        }, delay);
      };

      button.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        stop();
        count = 0;
        step();
        repeat(450);
      });
      ['pointerup', 'pointerleave', 'pointercancel'].forEach((type) => button.addEventListener(type, stop));
      // Keyboard activation (Enter / Space) arrives as a click with detail 0.
      button.addEventListener('click', (event) => {
        if (event.detail !== 0) return;
        count = 0;
        step();
      });
    }

    open() {
      const viewport = window.visualViewport;
      if (viewport && viewport.scale > 1.02) {
        this.say("You're zoomed in. Pinch the page back to normal size, then try again.");
        return;
      }
      this.say('');
      if (!this.overlay) this.overlay = this.buildOverlay();

      this.returnFocus = document.activeElement;
      document.documentElement.classList.add('brs-rs-lock');
      this.overlay.hidden = false;
      window.addEventListener('resize', this.onResize);
      document.addEventListener('keydown', this.onKeydown);

      const saved = store.get();
      const card = saved.card;
      this.cardPx = card ? card.px : CARD_SEEN * SEED_PX_PER_MM;
      this.size = saved.last || 10;
      const cardStillValid =
        card && card.width === layoutWidth() && Math.abs(card.dpr - window.devicePixelRatio) < 0.01;
      this.go(cardStillValid ? 'measure' : 'card');

      if (window.matchMedia('(pointer: coarse)').matches && this.overlay.requestFullscreen) {
        this.overlay.requestFullscreen({ navigationUI: 'hide' }).catch(() => {});
      }
    }

    close() {
      if (!this.overlay || this.overlay.hidden) return;
      if (document.fullscreenElement === this.overlay) document.exitFullscreen().catch(() => {});
      this.overlay.hidden = true;
      document.documentElement.classList.remove('brs-rs-lock');
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('keydown', this.onKeydown);
      if (this.returnFocus && this.returnFocus.focus) this.returnFocus.focus({ preventScroll: true });
    }

    go(step) {
      this.step = step;
      this.overlay.dataset.step = step;
      this.out('step').textContent = STEP_LABELS[step];
      this.render();
      const primary = this.overlay.querySelector(`[data-pane="${step}"] .brs-rs-primary`);
      if (primary) primary.focus({ preventScroll: true });
    }

    render() {
      if (this.step === 'card') {
        this.cardInput.max = String(Math.max(200, Math.floor(this.field.clientWidth)));
        this.setCard(this.cardPx);
      } else if (this.step === 'measure') {
        this.setSize(this.size);
      }
    }

    setCard(px) {
      this.cardPx = Math.min(Number(this.cardInput.max), Math.max(Number(this.cardInput.min), px));
      this.cardInput.value = String(this.cardPx);
      this.band.style.width = `${this.cardPx}px`;
      // The outline shares the strip's edges; its height and corners follow the card's shape.
      const outline = this.cardOutline.style;
      outline.width = `${this.cardPx}px`;
      outline.height = `${(this.cardPx * CARD_H) / CARD_W}px`;
      outline.borderRadius = `${(this.cardPx * CARD_R) / CARD_W}px`;
    }

    setSize(size) {
      this.size = Math.min(SIZE_MAX, Math.max(SIZE_MIN, Math.round(size * 2) / 2));
      this.out('size').textContent = formatSize(this.size);
      const pxPerMm = this.cardPx / CARD_SEEN;
      this.band.style.width = `${fingerSeen(insideDiameter(this.size - this.offset)) * pxPerMm}px`;
    }

    handle(action) {
      switch (action) {
        case 'close':
          this.close();
          break;
        case 'card-done':
          store.set({ card: { px: this.cardPx, width: layoutWidth(), dpr: window.devicePixelRatio } });
          this.go('measure');
          break;
        case 'recalibrate':
          this.go('card');
          break;
        case 'size-done':
          store.set({ last: this.size });
          this.out('result').textContent = formatSize(this.size);
          this.showLast(this.size);
          this.dispatchEvent(new CustomEvent('ring-sizer:result', { bubbles: true, detail: { size: this.size } }));
          this.go('result');
          break;
        case 'measure-again':
          this.go('measure');
          break;
      }
    }

    out(name) {
      return this.overlay.querySelector(`[data-out="${name}"]`);
    }

    showLast(size) {
      if (!size || !this.last) return;
      this.last.querySelector('[data-ring-sizer-last-size]').textContent = formatSize(size);
      this.last.hidden = false;
    }

    say(text) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.hidden = !text;
    }
  }

  customElements.define('brs-ring-sizer', BrsRingSizer);
})();
